/*
 * Copyright (c) 2021, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */
export default {
    baseStyle: (props) => {
        // Drive the quick-view button purely off the stable tileMode + CSS breakpoints,
        // never off the JS-resolved props.isDesktop. The latter is false for one frame on
        // a freshly-mounted tile (pagination), which briefly mismatched the CSS-driven
        // text visibility and produced an overlapping "+ / QUICK ADD" flash.
        const isQuickViewMode = props?.tileMode === 'quick-view'
        return {
            container: {
                position: 'relative',
                css: props?.showScrollHint
                    ? {
                          '@media screen and (max-width: 992px)': {
                              '@keyframes productTileMoveLeftRight': {
                                  '0%': {transform: 'translateX(0)'},
                                  '20%': {transform: 'translateX(-30px)'},
                                  '60%': {transform: 'translateX(-30px)'},
                                  '100%': {transform: 'translateX(0)'}
                              },
                              '&:first-of-type [data-pt-media-row="true"]': {
                                  animation: 'productTileMoveLeftRight 3s ease-in-out 1',
                                  animationDelay: '3s'
                              }
                          }
                      }
                    : undefined
            },
        favIcon: {
            position: 'absolute',
            variant: 'unstyled',
            top: {base: '8px', lg: '6px'},
            right: {base: '8px', lg: '6px'},
            zIndex: 2
        },
        favIconButton: {
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: {base: '16px', lg: '20px'},
            height: {base: '16px', lg: '20px'},
            minWidth: {base: '12px', lg: '20px'}
        },
        favIconSolid: {
            position: 'relative',
            width: {base: '16px', lg: '20px'},
            height: {base: '16px', lg: '20px'},
            color: 'webTheme.iconPrimary'
        },
        favIconOutline: {
            position: 'relative',
            width: {base: '16px', lg: '20px'},
            height: {base: '16px', lg: '20px'}
        },
        imageWrapper: {
            position: 'relative',
            marginBottom: 2,
            overflow: 'hidden'
        },
        image: {
            ratio: 19 / 28,
            paddingBottom: 2,
            width: '100%',
            transition: 'opacity 0.3s ease-in-out'
        },
        responsiveMediaBlock: {
            paddingBottom: {base: 2, lg: 0},
        },
        hoveredImageContainer: {
            position: ['relative', 'relative', 'relative', 'absolute', 'absolute'],
            width: '100%',
            height: '100%',
            top: 0,
            left: 0,
            zIndex: 1,
            opacity: [1, 1, 0, 0, 0],
            transition: 'opacity 0.3s ease-in-out'
        },
        link: {
            display: 'block',
            opacity: props?.isOOS ? '0.5' : '1',
            pointerEvents: props?.isOOS ? 'none' : 'auto',
            _hover: {
                textDecoration: 'none'
            }
        },
        titleRow: {
            justifyContent: 'space-between',
            alignItems: 'center'
        },
        wishlistVariantText: {
            textStyle: 'bodyXsRegular'
        },
        priceRow: {
            alignItems: 'flex-start'
        },
        promotionText: {
            textStyle: 'labelXs',
            marginBottom: {base: 1, lg: 0}
        },
        oosText: {
            color: 'webTypo.textSale',
            textStyle: 'badge',
            textTransform: 'uppercase',
            marginLeft: 2
        },
        swatchRow: {
            alignItems: 'center',
            marginBottom: {base: 1, lg: 0}
        },
        swatchItem: {
            width: '14px',
            height: '14px'
        },
        swatchImage: {
            height: '100%',
            width: '100%',
            backgroundRepeat: 'no-repeat',
            backgroundSize: 'cover'
        },
        colorSwatchCarouselContainer: {
            height: {base: 'unset', lg: '18px'}
        },
        colorSwatchRow: {
            display: 'flex',
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: 2,
            height: {base: 'unset', lg: '18px'}
        },
        colorSwatchItem: {
            width: 'auto'
        },
        colorSwatchImage: {
            height: '100%',
            width: '100%',
            minWidth: '32px',
            backgroundRepeat: 'no-repeat',
            backgroundSize: 'cover'
        },
        colorSwatchText: {
            height: '100%',
            width: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            textStyle: 'labelXs',
            letterSpacing: '0',
            // labelXs carries its own color; inherit so disabled swatches stay grey.
            color: 'inherit'
        },
        swatchCount: {
            textStyle: 'labelXs',
            whiteSpace: 'nowrap',
            marginLeft: 1
        },
        mediaScroller: {
            paddingBottom: 0,
            overflowX: props?.disableImageScroll ? 'hidden' : 'auto',
            css: {
                '&::-webkit-scrollbar': {
                    display: 'none'
                }
            }
        },
        mediaImageRow: {
            width: props?.showHoverImage ? {base: '200%', lg: 'full'} : 'full',
            display: 'flex'
        },
        mediaCarouselTrack: {
            display: 'flex',
            width: '200%',
            transition: 'transform 0.2s ease-in-out'
        },
        mediaCarouselSlide: {
            flex: '0 0 50%'
        },
        mediaAspect: {
            ratio: 19 / 28,
            paddingBottom: 0,
            width: '100%',
            transition: 'opacity 0.2s ease-in-out'
        },
        mediaCarouselNav: {
            position: 'absolute',
            top: '50%',
            left: 0,
            right: 0,
            transform: 'translateY(-50%)',
            justifyContent: 'space-between',
            paddingX: 1,
            zIndex: 2,
            pointerEvents: 'none'
        },
        mediaCarouselButton: {
            variant: 'unstyled',
            minWidth: '28px',
            height: '28px',
            padding: 1,
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: 'webTrasparency.neutralTransparencyWhite',
            backdropFilter: 'blur(10px)',
            borderRadius: 'none',
            pointerEvents: 'auto',
            _hover: {
                background: 'webTheme.backgroundDefault'
            }
        },
        mediaCarouselIcon: {
            width: '20px',
            height: '20px'
        },
        mediaHoverAspect: {
            position: ['relative', 'relative', 'relative', 'absolute', 'absolute'],
            width: '100%',
            height: '100%',
            top: 0,
            left: 0,
            zIndex: 1,
            transition: 'opacity 0.2s ease-in-out',
            // Mobile/tablet always show the hover media; desktop only on hover/touch.
            // CSS breakpoint (not JS props.isDesktop) so a freshly-mounted tile doesn't
            // flash the hover image before useBreakpointValue resolves.
            opacity: {
                base: 1,
                lg: props?.isHovered || props?.isTouch ? 1 : 0
            }
        },
        quickViewOverlay: {
            position: 'absolute',
            bottom: 0,
            width: 'full',
            // 4px inset on all sides (token 1) so the card floats off the tile edges.
            padding: 1,
            flexDirection: 'column',
            zIndex: 2,
            // Mobile always shows the overlay; desktop only on hover/touch/open. Driven by
            // the CSS breakpoint (not JS props.isDesktop) so a freshly-mounted tile doesn't
            // flash the overlay during the frame before useBreakpointValue resolves.
            opacity: {
                base: 1,
                lg: props?.isHovered || props?.isTouch || props?.isQuickViewOpen ? 1 : 0
            }
        },
        quickViewButton: {
            width: isQuickViewMode ? '24px' : {base: '24px', lg: '100%'},
            height: isQuickViewMode ? '24px' : {base: '24px', lg: '40px'},
            minWidth: 'auto',
            marginLeft: 'auto',
            padding: isQuickViewMode ? '2px' : {base: '2px', lg: null},
            right: isQuickViewMode ? 1 : {base: 1, lg: null},
            // Desktop bar sits on the overlay's own 4px inset, level with the open quick-add card.
            bottom: isQuickViewMode ? 1 : {base: 1, lg: 0},
            left: isQuickViewMode ? -1 : {base: 1, lg: 0},
            _hover: {
                '& .quick-view-icon': {
                    transform: {lg: 'translateY(-50%) rotate(180deg)'}
                }
            }
        },
        innerTextButton: {
            textStyle: 'ctaOther'
        },
        quickViewIcon: {
            color: isQuickViewMode ? undefined : {lg: 'webTheme.iconPrimary'},
            width: isQuickViewMode ? undefined : {lg: '24px'},
            height: isQuickViewMode ? undefined : {lg: '24px'},
            boxSize: isQuickViewMode ? undefined : {lg: 4},
            position: isQuickViewMode ? undefined : {lg: 'absolute'},
            right: isQuickViewMode ? undefined : {lg: '10px'},
        },
        skeletonStack: {
            spacing: 0
        },
        skeletonContainer: {
            spacing: 2
        },
        skeletonLinePrimary: {
            width: '80px',
            height: '18px'
        },
        skeletonLineSecondary: {
            width: {base: '120px', md: '220px'},
            height: '18px'
        },
        title: {
            color: 'webTypo.textPrimary',
            textStyle: 'labelXs',
            marginBottom: {base: 1, lg: 0},
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap'
        },
        rating: {},
        variations: {},
        badgeGroup: {
            position: 'absolute',
            ...(props?.isDesktop ? {top: 1} : {bottom: 2}),
            left: props?.isDesktop ? 1 : 2,
            zIndex: 5,
            alignItems: 'start'
        },
        badge: {
            padding: '7.5px 8px',
            display: 'inline-flex',
            justifyContent: 'center',
            alignItems: 'center',
            gap: '10px',
            background: 'whiteAlpha.750',
            backdropFilter: 'blur(5px)',
            color: 'webTypo.textPrimary',
            textStyle: 'badge',
            textAlign: 'center',
            textTransform: 'uppercase',
            borderRadius: 0
        },
        productLikesWrapper: {
            alignItems: 'center',
            gap: 0.5
        },
        productTileLabel: {
            color: 'webTypo.textSecondary',
            textStyle: {base: 'badge', lg: 'badge'},
            textTransform: 'uppercase',
            pointerEvents: 'none',
            marginTop: 1,
            marginBottom: 1
        },
        productTileLabelWrapper: {
            display: 'flex',
            height: {base: 'unset', lg: 5},
            marginBottom: {base: 0, lg: -0.5}
,           },
        /* Reserves the band under the image so tiles in a row stay level and
         * the hover-only swatch row doesn't shift the layout in. Title + price
         * is 40px; each extra row (a text badge, the swatch row) adds ~18px.
         * `detailsWrapper` covers one extra row, `detailsWrapperTall` both. */
        detailsWrapper: {
            minHeight: {base: 'unset', lg: '58px'}
        },
        detailsWrapperTall: {
            minHeight: {base: 'unset', lg: '80px'}
        },
        videoTileWrapper: {
            position: 'relative',
            width: '100%',
            paddingBottom: 'calc(28 / 19 * 100%)', // matches mediaAspect ratio: 19/28
            overflow: 'hidden'
        },
        videoTileElement: {
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            display: 'block'
        }
        }
    },
    variants: {
        wishlist: {
            link: {
                pointerEvents: 'auto'
            }
        },
        VerticalList: {
            link: {
                boxSizing: 'border-box',
                overflow: 'auto',
                display: 'block'
            },
            imageWrapper: {
                float: 'left',
                width: {base: '95px', lg: '225px'},
                boxSizing: 'border-box',
                marginBottom: 0
            },
            bodyText: {
                marginLeft: {base: '95px', lg: '225px'},
                marginTop: '0',
                marginBottom: '0',
                padding: {base: '0 0 0 8px'},
                boxSizing: 'border-box',
                clear: 'none',
            },
            title: {
                textOverflow: 'unset',
                whiteSpace: 'unset'
            }
        },
        ModalVerticalList: {
            imageWrapper: {
                float: 'left',
                width: {base: '95px', lg: '95px'},
                boxSizing: 'border-box',
                marginBottom: 0
            },
            bodyText: {
                marginLeft: {base: '95px', lg: '95px'},
                marginTop: '0',
                marginBottom: '0',
                padding: {base: '0 0 0 8px'},
                boxSizing: 'border-box',
                clear: 'none'
            },
            title: {
                textOverflow: 'unset',
                whiteSpace: 'unset'
            }
        }
    },
    parts: [
        'container',
        'favIcon',
        'favIconButton',
        'favIconSolid',
        'favIconOutline',
        'imageWrapper',
        'bodyText',
        'image',
        'hoveredImageContainer',
        'link',
        'price',
        'title',
        'titleRow',
        'wishlistVariantText',
        'priceRow',
        'promotionText',
        'oosText',
        'swatchRow',
        'swatchItem',
        'swatchImage',
        'swatchCount',
        'mediaScroller',
        'mediaImageRow',
        'mediaAspect',
        'mediaHoverAspect',
        'quickViewOverlay',
        'innerTextButton',
        'quickViewButton',
        'quickViewIcon',
        'skeletonStack',
        'skeletonContainer',
        'skeletonLinePrimary',
        'skeletonLineSecondary',
        'rating',
        'variations',
        'badgeGroup',
        'badge',
        'productLikesWrapper',
        'productTileLabel',
        'productTileLabelWrapper',
        'detailsWrapper',
        'detailsWrapperTall',
        'videoTileWrapper',
        'videoTileElement'
    ]
}
